import { ChevronRight, ScrollText, Workflow } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { api, isApiError } from '../../api';
import { Badge } from '../../components/Badge';
import { Callout, Section } from '../../components/blocks';
import { DataError, OpsReasonBar } from '../../components/DataError';
import { CorrelationLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { logsLink, TRACE_ID_RE } from '../../lib/opsQuery';
import { formatMs, parseTempoTrace, type ParsedTrace, type TraceSpan } from '../../lib/tempo';
import { useApi } from '../../lib/useApi';
import { JsonBlock, LinkButton } from './shared';

const serviceColor = (i: number): string => `var(--chart-${(i % 5) + 1})`;

function Waterfall({ trace }: { trace: ParsedTrace }) {
  const { t } = useTranslation('ops');
  const [open, setOpen] = useState<string | null>(null);
  const total = Math.max(0.001, trace.endMs - trace.startMs);
  const serviceIndex = new Map(trace.services.map((s, i) => [s, i]));
  return (
    <div className="overflow-x-auto" role="group" aria-label={t('trace.waterfall.label')}>
      <div className="min-w-[640px]">
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 border-b bg-muted/40 px-4 py-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          <span>{t('trace.waterfall.span')}</span>
          <span>{t('trace.waterfall.timeline', { total: formatMs(total) })}</span>
        </div>
        {trace.spans.map((s: TraceSpan) => {
          const left = ((s.startMs - trace.startMs) / total) * 100;
          const width = Math.max(0.4, (s.durationMs / total) * 100);
          const color = serviceColor(serviceIndex.get(s.service) ?? 0);
          const expanded = open === s.id;
          return (
            <div key={s.id || `${s.name}-${s.startMs}`} className={cn('border-b last:border-b-0', s.error && 'bg-critical-soft/40')}>
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-center gap-3 px-4 py-1.5">
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-1.5 rounded text-left text-sm hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  style={{ paddingLeft: `${Math.min(s.depth, 12) * 12 + 4}px` }}
                  onClick={() => setOpen(expanded ? null : s.id)}
                  aria-expanded={expanded}
                >
                  <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-90')} aria-hidden="true" />
                  <span className="size-2.5 shrink-0 rounded-sm" style={{ background: color }} aria-hidden="true" />
                  <span className="shrink-0 font-medium">{s.service}</span>
                  <span className="truncate font-mono text-xs text-muted-foreground">{s.name}</span>
                  {s.error ? <Badge tone="crit">{t('trace.waterfall.error')}</Badge> : null}
                </button>
                <span className="relative h-5">
                  <span className="absolute top-1.5 h-2 rounded-sm" style={{ left: `${left}%`, width: `${width}%`, background: color }} />
                  <span
                    className="tabular absolute top-0 text-xs whitespace-nowrap text-muted-foreground"
                    style={left + width > 80 ? { right: `${100 - left}%`, marginRight: 4 } : { left: `${left + width}%`, marginLeft: 4 }}
                  >
                    {formatMs(s.durationMs)}
                  </span>
                </span>
              </div>
              {expanded ? (
                <div className="space-y-4 border-t bg-muted/20 px-4 py-4">
                  <KeyValue
                    columns={3}
                    rows={[
                      [t('trace.waterfall.spanId'), <code key="id" className="text-xs">{s.id}</code>],
                      [t('trace.waterfall.parent'), s.parentId ? <code key="p" className="text-xs">{s.parentId}</code> : t('trace.waterfall.root')],
                      [t('trace.waterfall.kind'), s.kind ?? '—'],
                      [t('trace.waterfall.startOffset'), formatMs(s.startMs - trace.startMs)],
                      [t('trace.waterfall.duration'), formatMs(s.durationMs)],
                      s.error ? [t('trace.waterfall.status'), <span key="e" className="font-medium text-critical">{s.error}</span>] : false,
                    ]}
                  />
                  {Object.keys(s.attributes).length ? (
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
                      {Object.entries(s.attributes).map(([k, v]) => (
                        <div key={k} className="min-w-0">
                          <dt className="font-mono text-muted-foreground">{k}</dt>
                          <dd className="font-mono break-all">{k === 'correlation_id' ? <CorrelationLink id={v} n={36} /> : v}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** `/ops/traces/:id`: one trace as a span waterfall (Tempo JSON via `/ops/v1/traces/{id}`). */
export function TracePage() {
  const { t } = useTranslation('ops');
  const { id = '' } = useParams();
  const traceId = id.toLowerCase();
  useDocumentTitle(`${t('trace.title')} ${traceId.slice(0, 12)}`);
  const valid = TRACE_ID_RE.test(traceId);
  const result = useApi((signal) => (valid ? api.getTrace(traceId, { signal }) : Promise.resolve(null)), [traceId, valid]);
  const crumbs = [{ label: t('crumb') }, { label: t('trace.crumb'), to: '/ops/traces' }, { label: `${traceId.slice(0, 12)}…` }];
  const parsed = result.status === 'success' ? parseTempoTrace(result.data) : null;
  const correlation = parsed?.spans.map((s) => s.attributes.correlation_id).find(Boolean) ?? null;
  const notFound = result.status === 'error' && isApiError(result.error) && result.error.status === 404;

  return (
    <>
      <PageHeader title={t('trace.title')} crumbs={crumbs} subtitle={<CopyText value={traceId} />} />
      <div className="space-y-6">
        {!valid ? (
          <Callout tone="warn">{t('trace.invalid', { id })}</Callout>
        ) : null}
        {valid ? <OpsReasonBar /> : null}
        {result.status === 'loading' ? <Loading label={t('trace.loading')} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('trace.notFound') : t('trace.failed')} /> : null}
        {result.status === 'success' && valid && !parsed ? (
          <Section>
            <EmptyState title={t('trace.noSpans')}>{t('trace.noSpansBody')}</EmptyState>
            <div className="mt-4">
              <JsonBlock label={t('shared.rawResponse')} value={result.data} />
            </div>
          </Section>
        ) : null}
        {parsed ? (
          <>
            <Section
              actions={
                <>
                  <LinkButton to={logsLink(`{service=~".+"} | json | trace_id="${traceId}"`, '7d')}>
                    <ScrollText /> {t('trace.logsFor')}
                  </LinkButton>
                  {correlation ? (
                    <LinkButton to={`/ops/correlate/${encodeURIComponent(correlation)}`}>
                      <Workflow /> {t('trace.correlation')}
                    </LinkButton>
                  ) : null}
                </>
              }
              title={parsed.spans[0] ? `${parsed.spans[0].service}: ${parsed.spans[0].name}` : t('trace.title')}
            >
              <KeyValue
                columns={3}
                rows={[
                  [t('trace.field.started'), formatDateTime(new Date(parsed.startMs).toISOString())],
                  [t('trace.field.duration'), formatMs(parsed.endMs - parsed.startMs)],
                  [t('trace.field.spans'), parsed.spans.length],
                  [t('trace.field.services'), parsed.services.join(', ')],
                  [
                    t('trace.field.errors'),
                    parsed.errorCount ? (
                      <Badge key="e" tone="crit">
                        {parsed.errorCount}
                      </Badge>
                    ) : (
                      <Badge key="e" tone="ok">
                        {t('shared.none')}
                      </Badge>
                    ),
                  ],
                ]}
              />
            </Section>
            <Tabs defaultValue="waterfall">
              <TabsList>
                <TabsTrigger value="waterfall">{t('trace.tab.waterfall')}</TabsTrigger>
                <TabsTrigger value="raw">{t('trace.tab.raw')}</TabsTrigger>
              </TabsList>
              <TabsContent value="waterfall">
                <Section flush className="overflow-hidden pt-0">
                  <Waterfall trace={parsed} />
                </Section>
              </TabsContent>
              <TabsContent value="raw">
                <JsonBlock label={t('shared.rawResponse')} value={result.data} open />
              </TabsContent>
            </Tabs>
          </>
        ) : null}
      </div>
    </>
  );
}
